import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import useApi from '../../../../hook/useApi';
import { ButtonPrimary } from '../../../../utils/Button';
import cn from '../../../../utils/cn';
import { GetDate } from '../../../../utils/Show';
import { DownArrowIcon } from '../../../../utils/SVGIcon';
import { TextCapitalize } from '../../../../utils/TextFormatter';
import { mkRelativeUrl } from '../../../../utils/URL';
import { useAgency } from '../../AgencyProvider';
import ModalAgencyCustomerStatusChange from '../Modals/ModalAgencyCustomerStatusChange';

function TableInfoCard({ name, value, isCapitalize, className }) {
    return (
        <div
            className={cn(
                'text-sm text-primary-700 font-medium flex flex-row gap-3 justify-start items-center h-5',
                className
            )}
        >
            <p className="inline-flex justify-between items-center min-w-[112px] w-28">
                <span>{name}</span>
                <span>:</span>
            </p>
            <span
                className={cn(`value-text line-clamp-1 text-primary font-normal text-sm`, {
                    capitalize: isCapitalize
                })}
            >
                {value}
            </span>
        </div>
    );
}

function TableRow({ item, setActiveId, activeId }) {
    const navigate = useNavigate();
    const { setConfirm, openModal, setMessage } = useTemplate();
    const { handleReload } = useAgency();
    const { put } = useApi();

    const [isActive, setIsActive] = useState(false);

    const handleView = (ev) => {
        ev.stopPropagation();
        navigate(mkRelativeUrl(`/agency/${item?.id}/customers`));
    };

    // -- Change Customer Status Handler
    const handleAgencyStatusModal = (e, data) => {
        e.stopPropagation();
        setConfirm({
            showButton: false,
            text: <ModalAgencyCustomerStatusChange data={data} onReload={handleReload} />
        });
    };
    const isExpanded = activeId === item.id;

    // -- Check if the user is a customer (no setActiveId function passed)
    const isCustomer = typeof setActiveId !== 'function';

    const isUnlocated = item?.agencyId === -9191;

    const handleSelectRow = (ev) => {
        setIsActive((prev) => !prev);
        if (!isCustomer) {
            setActiveId(isExpanded ? -1 : item.id);
            return;
        }

        ev.stopPropagation();
        ev.preventDefault();
    };

    const handleDisable2FA = (e) => {
        e.stopPropagation();
        setConfirm({
            text: 'Disabling Two-Factor Authentication will remove an additional layer of security on this account. Are you sure you want to proceed?',
            textAction: 'Yes',
            clickAction: () => {
                put(`/api/agency/${item.id}/disable-2fa`)
                    .then(({ title }) => {
                        setMessage({ type: 'success', text: title });
                        setConfirm(null);
                        handleReload();
                    })
                    .catch((err) => {
                        setMessage({ type: 'error', text: err.toString() });
                    });
            }
        });
    };

    return (
        <div
            tabIndex={-1}
            role="button"
            onClick={handleSelectRow}
            className={cn(
                `table-visible-data overflow-hidden w-full flex flex-col justify-between text-primary p-2.5 rounded-md transition-all duration-[800ms] ease-in-out remove-highlight`,
                {
                    'max-h-[700px]': isActive,
                    'max-h-10': !isActive,
                    'placeholder-mode': item.demo
                }
            )}
        >
            <div className="w-full flex flex-row gap-1 justify-between text-primary text-sm font-medium leading-normal">
                <div className="min-w-[112px] w-28 flex items-center gap-1">
                    {!isCustomer && (
                        <button
                            type="button"
                            className={cn(`-rotate-90 transition-all duration-200`, {
                                'rotate-0': isActive
                            })}
                        >
                            <DownArrowIcon />
                        </button>
                    )}
                    <span>{item?.id}</span>
                </div>
                <div className="w-full">
                    <span className="line-clamp-1">
                        {' '}
                        {item?.organization || item?.fullName || '-----'}
                    </span>
                </div>
                <div className="w-full hidden sm:block">
                    <span
                        className={`line-clamp-1 capitalize ${
                            item?.status === 'active' ? 'text-success' : 'text-warning'
                        }`}
                    >
                        {item?.status}
                    </span>
                </div>
                {isCustomer && (
                    <button
                        type="button"
                        className={cn(`-rotate-90 transition-all duration-200 block`, {
                            'rotate-0': isActive
                        })}
                        onClick={handleSelectRow}
                    >
                        <DownArrowIcon />
                    </button>
                )}
            </div>

            <div className="table-data-more mt-3.5 flex flex-col justify-start items-item gap-2">
                <TableInfoCard
                    name="Organization"
                    value={
                        item.organization ? (
                            <Link
                                to={`/agency/${item?.id}/customers`}
                                className="text-primary-700 underline font-medium"
                            >
                                {item.organization}
                            </Link>
                        ) : (
                            '--'
                        )
                    }
                />
                <TableInfoCard name="Role" value={TextCapitalize(item?.role)} />
                <TableInfoCard name="Email" value={item?.primaryEmail} />
                <TableInfoCard
                    name="Date Created"
                    value={GetDate(item?.timeCreated)}
                    isCapitalize
                />
                {/* <TableInfoCard
                    name="Currency"
                    value={GetNumberWithZero(item?.currency)}
                    isCapitalize
                /> */}
                <TableInfoCard
                    name="2FA"
                    className={`[&>.value-text]:${item?.tfaEnabled ? 'text-secondary' : 'text-error'}`}
                    value={item?.tfaEnabled ? 'Enabled' : 'Disabled'}
                />
                <TableInfoCard
                    name="Status"
                    value={item?.status}
                    isCapitalize
                    className={`sm:hidden ${item?.status === 'active' ? '[&>.value-text]:text-success' : '[&>.value-text]:text-warning'}`}
                />
                <div className="flex flex-wrap items-center justify-end gap-2 mt-2">
                    {!isUnlocated && isActive && (
                        <ButtonPrimary
                            btnType="button"
                            title="Edit Info"
                            onClick={() => openModal('UPDATE_AGENCY_INFO_LIST_MODAL', item)}
                            // onClick={() => openModal('update-agency-info-modal', item)}
                            className="h-7 text-sm text-nowrap whitespace-nowrap"
                        />
                    )}
                    {!isUnlocated && (
                        <ButtonPrimary
                            btnType="button"
                            title={isActive ? 'Make Inactive' : 'Make Active'}
                            onClick={(ev) =>
                                handleAgencyStatusModal(ev, {
                                    id: item?.id,
                                    status: item?.status
                                })
                            }
                            className="h-7 text-sm text-nowrap whitespace-nowrap"
                        />
                    )}
                    {isUnlocated && isCustomer && (
                        <ButtonPrimary
                            btnType="button"
                            title={isActive ? 'Make Inactive' : 'Make Active'}
                            onClick={(ev) =>
                                handleAgencyStatusModal(ev, {
                                    id: item?.id,
                                    status: item?.status
                                })
                            }
                            className="h-7 text-sm text-nowrap whitespace-nowrap"
                        />
                    )}

                    {!isUnlocated && isActive && (
                        <ButtonPrimary
                            btnType="button"
                            title="Create Customer"
                            onClick={() => openModal('create-agency-customer-modal', item)}
                            className="h-7 text-sm text-nowrap whitespace-nowrap"
                        />
                    )}

                    {!isUnlocated && item?.tfaEnabled && isActive && (
                        <ButtonPrimary
                            btnType="button"
                            title="Disable 2FA"
                            onClick={handleDisable2FA}
                            className="h-7 text-sm text-nowrap whitespace-nowrap"
                        />
                    )}
                    {!isUnlocated && (
                        <ButtonPrimary
                            btnType="button"
                            title="Manage"
                            onClick={handleView}
                            className="h-7 text-sm "
                        />
                    )}
                    {isUnlocated && isCustomer && (
                        <ButtonPrimary
                            btnType="button"
                            title="Move to agency"
                            onClick={() => openModal('move-to-under-agency', item)}
                            className="h-7 text-sm "
                        />
                    )}
                </div>
            </div>
            {/* 
            {isActive &&
                item.customerList.map((customer) => <TableRow key={customer.id} item={customer} />)} */}
            {item?.customerList?.map((customer) => (
                <TableRow key={customer.id} item={customer} />
            ))}
        </div>
    );
}

function AgencyPageTableMobile({ data }) {
    const [activeId, setActiveId] = useState(-1);
    return (
        <div className="invoice_list_page_table">
            <div className="table-data [&>*:nth-child(odd)]:bg-primary-50">
                {data && data?.length !== 0
                    ? data?.map((item) => (
                          <TableRow
                              key={item.id}
                              item={item}
                              setActiveId={setActiveId}
                              activeId={activeId}
                          />
                      ))
                    : null}
                {data && data?.length === 0 ? (
                    <div className="w-full text-sm font-medium p-2 rounded-5">No Data Found</div>
                ) : null}
            </div>
        </div>
    );
}

export default AgencyPageTableMobile;
